/**
 * Management Accounts — /finance/reports/management-pack
 * All-in-one pack: P&L + Balance Sheet KPIs + Cash Flow + Revenue by type.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PieChart, RefreshCw, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ManPackData {
  reportType: string;
  period: { from: string; to: string };
  generatedAt: string;
  pl: {
    revenue: number;
    cogs: number;
    grossProfit: number;
    expenses: number;
    netProfit: number;
    margin: number;
  };
  balanceSheet: { assets: number; liabilities: number; equity: number };
  cashFlow: { inflows: number; outflows: number; net: number };
  revenueByType: { type: string; total: number }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear(): { from: string; to: string } {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

function KpiCard({ label, value, sub, color = '' }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className={`text-xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ManagementAccountsPage() {
  const [data, setData]           = useState<ManPackData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [from, setFrom]           = useState(thisYear().from);
  const [to, setTo]               = useState(thisYear().to);
  const [narrative, setNarrative] = useState<string>('');
  const [narLoading, setNarLoading] = useState(false);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      const res = await api.get<ManPackData>(`/finance/reports/management-pack?from=${f}&to=${t}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to);
  }, [fetchData, from, to]);

  const run = () => { loadedRef.current = false; setNarrative(''); fetchData(from, to); };

  const generateNarrative = async () => {
    if (!data) return;
    setNarLoading(true);
    try {
      const now = new Date();
      const period = `${from} to ${to}`;
      const res = await api.post<{ narrative: string }>('/finance/audit/narrative', {
        revenue:          data.pl.revenue,
        grossProfit:      data.pl.grossProfit,
        netProfit:        data.pl.netProfit,
        totalAssets:      data.balanceSheet.assets,
        totalLiabilities: data.balanceSheet.liabilities,
        equity:           data.balanceSheet.equity,
        operatingCashFlow: data.cashFlow.net,
        period,
        currency:         'SGD',
      });
      setNarrative(res?.narrative ?? '');
    } catch {
      setNarrative('Unable to generate narrative at this time.');
    } finally {
      setNarLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <PieChart className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Management Accounts</h1>
          <p className="text-sm text-muted-foreground">Combined P&L · Balance Sheet · Cash Flow</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-3 flex flex-wrap gap-2 items-center">
          <label className="text-xs text-muted-foreground">From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="text-sm border rounded px-2 py-1" />
          <label className="text-xs text-muted-foreground">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="text-sm border rounded px-2 py-1" />
          <Button size="sm" onClick={run} disabled={loading} className="ml-auto">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Run
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading management pack…</div>
      ) : !data ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No data available for selected period.</div>
      ) : (
        <>
          {/* Top KPI Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KpiCard label="Revenue" value={fmt(data.pl.revenue)} sub="Total sales" />
            <KpiCard
              label="Gross Profit"
              value={fmt(data.pl.grossProfit)}
              sub={`GP margin: ${data.pl.revenue > 0 ? ((data.pl.grossProfit / data.pl.revenue) * 100).toFixed(1) : '0'}%`}
              color={data.pl.grossProfit >= 0 ? 'text-green-700' : 'text-red-600'}
            />
            <KpiCard
              label="Net Profit / (Loss)"
              value={fmt(data.pl.netProfit)}
              sub={`Net margin: ${data.pl.margin}%`}
              color={data.pl.netProfit >= 0 ? 'text-green-700' : 'text-red-600'}
            />
            <KpiCard
              label="Net Cash"
              value={fmt(data.cashFlow.net)}
              sub={`In: ${fmt(data.cashFlow.inflows)}`}
              color={data.cashFlow.net >= 0 ? 'text-blue-700' : 'text-red-600'}
            />
          </div>

          {/* P&L + BS + CF side by side */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* P&L */}
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-3">Income Statement</p>
                {([
                  { label: 'Revenue',             val: data.pl.revenue,     indent: false, bold: false, color: '' },
                  { label: 'Cost of Sales',        val: -data.pl.cogs,       indent: true,  bold: false, color: 'text-red-600' },
                  { label: 'Gross Profit',         val: data.pl.grossProfit, indent: false, bold: true,  color: data.pl.grossProfit >= 0 ? 'text-green-700' : 'text-red-600' },
                  { label: 'Operating Expenses',   val: -data.pl.expenses,   indent: true,  bold: false, color: 'text-red-600' },
                  { label: 'Net Profit / (Loss)',  val: data.pl.netProfit,   indent: false, bold: true,  color: data.pl.netProfit >= 0 ? 'text-green-700' : 'text-red-600' },
                ] as const).map(row => (
                  <div
                    key={row.label}
                    className={`flex justify-between text-xs py-1 border-b last:border-0 ${row.indent ? 'pl-3' : ''} ${row.bold ? 'font-semibold' : ''}`}
                  >
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className={`tabular-nums ${row.color}`}>{fmt(row.val)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Balance Sheet */}
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-3">Balance Sheet</p>
                {([
                  { label: 'Total Assets',      val: data.balanceSheet.assets,      color: 'text-blue-700' },
                  { label: 'Total Liabilities', val: data.balanceSheet.liabilities, color: 'text-orange-600' },
                  { label: 'Total Equity',      val: data.balanceSheet.equity,      color: 'text-purple-700' },
                ] as const).map(row => (
                  <div key={row.label} className="flex justify-between text-xs py-1 border-b last:border-0">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className={`tabular-nums font-semibold ${row.color}`}>{fmt(row.val)}</span>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t">
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold">Liabilities + Equity</span>
                    <span className="tabular-nums font-semibold">
                      {fmt(data.balanceSheet.liabilities + data.balanceSheet.equity)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cash Flow */}
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-3">Cash Flow</p>
                <div className="flex justify-between text-xs py-1 border-b items-center">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-green-500" /> Cash Receipts
                  </span>
                  <span className="tabular-nums text-green-700 font-semibold">{fmt(data.cashFlow.inflows)}</span>
                </div>
                <div className="flex justify-between text-xs py-1 border-b items-center">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <TrendingDown className="h-3 w-3 text-orange-500" /> Cash Payments
                  </span>
                  <span className="tabular-nums text-orange-600 font-semibold">({fmt(data.cashFlow.outflows)})</span>
                </div>
                <div className={`flex justify-between text-xs py-2 mt-1 border-t font-bold items-center`}>
                  <span className="flex items-center gap-1">
                    <Minus className="h-3 w-3" /> Net Cash
                  </span>
                  <span className={`tabular-nums ${data.cashFlow.net >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {fmt(data.cashFlow.net)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Narrative */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <p className="text-sm font-semibold">AI Management Commentary</p>
                </div>
                <Button size="sm" variant="outline" onClick={generateNarrative} disabled={narLoading}>
                  <Sparkles className={`h-3.5 w-3.5 mr-1 ${narLoading ? 'animate-pulse text-purple-500' : ''}`} />
                  {narLoading ? 'Generating…' : narrative ? 'Regenerate' : 'Generate'}
                </Button>
              </div>
              {narrative ? (
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{narrative}</p>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  Click Generate to create an AI-powered board-level commentary on these results.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Revenue by Type */}
          {data.revenueByType.length > 0 && (
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-3">Revenue by Entry Type</p>
                <div className="space-y-2">
                  {data.revenueByType.map(row => {
                    const pct = data.pl.revenue > 0 ? (row.total / data.pl.revenue) * 100 : 0;
                    return (
                      <div key={row.type}>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="font-mono">{row.type}</span>
                          <span className="tabular-nums text-muted-foreground">{fmt(row.total)} ({pct.toFixed(1)}%)</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
